import { useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { getEvaluationPdfUrl, type EvaluationItem } from './talentEvaluationsApi';
import { useNotify } from '../../lib/notify';

/**
 * Render ramificado por <c>type</c>:
 *   - 0 (PdfDocument): card con título + "View PDF" → click pide signed URL + abre en
 *     nueva tab (window.open). El URL no se persiste — cada click pide uno fresh
 *     (queda short-lived hasta que el browser lo abre).
 *   - 1 (NumericScore): card con título + score grande (con maxScore como denominador
 *     si aplica) + provider chip.
 *   - 2 (ExternalEmbed): card con título + provider chip + "Open on [provider]" link
 *     (target="_blank", URL pública sin signed).
 */
export function EvaluationCard({
  evaluation,
  shortlistPublicKey,
  talentPublicKey,
}: {
  evaluation: EvaluationItem;
  shortlistPublicKey: string;
  talentPublicKey: string;
}) {
  switch (evaluation.type) {
    case 0:
      return (
        <PdfEvaluation
          evaluation={evaluation}
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
        />
      );
    case 1:
      return <ScoreEvaluation evaluation={evaluation} />;
    case 2:
      return <EmbedEvaluation evaluation={evaluation} />;
    default:
      return null;
  }
}

function PdfEvaluation({
  evaluation,
  shortlistPublicKey,
  talentPublicKey,
}: {
  evaluation: EvaluationItem;
  shortlistPublicKey: string;
  talentPublicKey: string;
}) {
  const { t } = useTranslation();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  async function handleOpen() {
    if (loading) return;
    setLoading(true);
    try {
      const result = await getEvaluationPdfUrl({
        shortlistPublicKey,
        talentPublicKey,
        evaluationPublicKey: evaluation.id,
      });
      // Abrir en nueva tab. El user puede ver el PDF inline si el browser lo soporta.
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch {
      notify.error(t('opportunities.talent.evaluation.error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      data-testid={`evaluation-card-${evaluation.id}`}
      data-evaluation-type="pdf"
      sx={cardSx}
    >
      <CardHeader title={evaluation.title} kind={t('opportunities.talent.evaluation.kindPdf')} />
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        disabled={loading}
        data-testid={`evaluation-open-pdf-${evaluation.id}`}
        sx={{
          mt: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          borderRadius: '10px',
          background: tokens.color.accent,
          color: tokens.color.accentInk,
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          border: 0,
          cursor: loading ? 'wait' : 'pointer',
          opacity: loading ? 0.7 : 1,
          '&:hover': { opacity: loading ? 0.7 : 0.92 },
        }}
      >
        {loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : '📄'}
        {t('opportunities.talent.evaluation.viewPdf')}
      </Box>
    </Box>
  );
}

function ScoreEvaluation({ evaluation }: { evaluation: EvaluationItem }) {
  const { t } = useTranslation();
  const score = evaluation.score ?? 0;
  const max = evaluation.maxScore;
  const percent = max && max > 0 ? Math.round((score / max) * 100) : null;

  return (
    <Box
      data-testid={`evaluation-card-${evaluation.id}`}
      data-evaluation-type="score"
      sx={cardSx}
    >
      <CardHeader title={evaluation.title} kind={t('opportunities.talent.evaluation.kindScore')} provider={evaluation.provider} />
      <Box sx={{ mt: '12px', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
        <Typography
          data-testid={`evaluation-score-${evaluation.id}`}
          sx={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', color: tokens.color.ink }}
        >
          {formatScore(score)}
        </Typography>
        {max != null && (
          <Typography sx={{ fontSize: 14, color: tokens.color.muted }}>
            / {formatScore(max)}
          </Typography>
        )}
        {percent != null && (
          <Box
            component="span"
            data-testid={`evaluation-percent-${evaluation.id}`}
            sx={{
              ml: '10px',
              padding: '2px 8px',
              borderRadius: tokens.size.radiusPill,
              background: percent >= 70 ? tokens.color.posSoft : tokens.color.surface2,
              color: percent >= 70 ? tokens.color.pos : tokens.color.muted,
              fontFamily: tokens.font.mono,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.08em',
            }}
          >
            {percent}%
          </Box>
        )}
      </Box>
    </Box>
  );
}

function EmbedEvaluation({ evaluation }: { evaluation: EvaluationItem }) {
  const { t } = useTranslation();
  const url = evaluation.externalUrl;
  if (!url || !isSafeHttpUrl(url)) return null;

  return (
    <Box
      data-testid={`evaluation-card-${evaluation.id}`}
      data-evaluation-type="embed"
      sx={cardSx}
    >
      <CardHeader title={evaluation.title} kind={t('opportunities.talent.evaluation.kindEmbed')} provider={evaluation.provider} />
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`evaluation-open-embed-${evaluation.id}`}
        sx={{
          mt: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          borderRadius: '10px',
          background: 'transparent',
          color: tokens.color.accent,
          border: `1px solid ${tokens.color.accent}`,
          fontSize: 13,
          fontWeight: 600,
          textDecoration: 'none',
          '&:hover': { background: tokens.color.accentSoft },
        }}
      >
        ↗
        {t('opportunities.talent.evaluation.openExternal', {
          provider: evaluation.provider ?? '',
        })}
      </Box>
    </Box>
  );
}

function CardHeader({
  title,
  kind,
  provider,
}: {
  title: string;
  kind: string;
  provider?: string | null;
}) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
        }}
      >
        {kind}
        {provider ? ` · ${provider}` : ''}
      </Typography>
      <Typography sx={{ mt: '4px', fontWeight: 600, fontSize: 15, color: tokens.color.ink }}>
        {title}
      </Typography>
    </Box>
  );
}

function formatScore(n: number): string {
  // Mostrar como entero si es entero, sino con 1 decimal.
  return Number.isInteger(n) ? n.toString() : n.toFixed(1);
}

// Bloquea schemes peligrosos (javascript:, data:, vbscript:, file:, etc) que
// podrían convertir un href controlado por recruiter en XSS si llegaran a colarse.
// Solo permite http(s) absolutos.
function isSafeHttpUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

const cardSx = {
  display: 'flex',
  flexDirection: 'column',
  padding: '14px 16px',
  borderRadius: '12px',
  background: tokens.color.surface2,
  border: `1px solid ${tokens.color.line}`,
};
